import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const INDUSTRIES = ["SaaS", "AI/ML", "Fintech", "Healthtech", "Climate", "Consumer", "Marketplaces", "Devtools", "Web3", "Deep Tech", "Other"];

const initial = {
  founder_name: "", email: "", phone: "", startup_name: "", website: "",
  country: "", year_incorporation: new Date().getFullYear(), industry: "SaaS",
  one_liner: "", long_description: "",
};

const inputCls = "bg-black border-white/10 text-white placeholder:text-white/40 rounded-none focus-visible:ring-lime h-12";

export default function Apply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, year_incorporation: parseInt(form.year_incorporation) || new Date().getFullYear() };
      const { data } = await api.post("/applications", payload);
      setResult(data);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-[720px] mx-auto px-6 md:px-10 py-24 text-center" data-testid="apply-success">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">Application received</div>
        <h1 className="font-heading text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
          You're in the queue.
        </h1>
        <p className="mt-6 text-white/70 text-lg leading-relaxed">
          We'll email you if you're shortlisted. In the meantime, sharpen your pitch — you'll get 10 minutes on live.
        </p>
        <div className="mt-10 bg-surface border border-white/10 p-6 text-left">
          <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Your reference</div>
          <div className="font-mono text-lime text-sm break-all">{result.application_id}</div>
        </div>
        <a href="/episodes" className="mt-10 inline-block px-8 py-4 border border-white/20 font-heading font-bold uppercase tracking-wider hover:border-lime hover:text-lime transition-colors">
          Watch past episodes →
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16" data-testid="apply-page">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-lime mb-6">Application</div>
      <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
        Pitch me.<br /><span className="text-white/50">Live.</span> <span className="text-lime">€20K.</span>
      </h1>
      <p className="mt-6 text-white/70 text-lg max-w-2xl">
        First-come-first-served among the selected ones. No warm intros needed. Fill it out honestly — if you're shortlisted, we'll email you the date for your video recording.
      </p>

      <form onSubmit={submit} className="mt-14 space-y-8" data-testid="apply-form">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Founder name *</Label>
            <Input data-testid="input-founder-name" required value={form.founder_name} onChange={set("founder_name")} className={`mt-2 ${inputCls}`} placeholder="Jane Doe" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Email *</Label>
            <Input data-testid="input-email" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="jane@startup.com" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Phone *</Label>
            <Input data-testid="input-phone" required value={form.phone} onChange={set("phone")} className={`mt-2 ${inputCls}`} placeholder="+352 621 000 000" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Startup name *</Label>
            <Input data-testid="input-startup-name" required value={form.startup_name} onChange={set("startup_name")} className={`mt-2 ${inputCls}`} placeholder="Rocket Inc." />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Website *</Label>
            <Input data-testid="input-website" required value={form.website} onChange={set("website")} className={`mt-2 ${inputCls}`} placeholder="https://…" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Country *</Label>
            <Input data-testid="input-country" required value={form.country} onChange={set("country")} className={`mt-2 ${inputCls}`} placeholder="Luxembourg" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Year founded *</Label>
            <Input data-testid="input-year" required type="number" min="1900" max="2100" value={form.year_incorporation} onChange={set("year_incorporation")} className={`mt-2 ${inputCls}`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Industry *</Label>
            <select
              data-testid="input-industry"
              value={form.industry}
              onChange={set("industry")}
              className="mt-2 w-full bg-black border border-white/10 h-12 px-3 text-white focus:outline-none focus:ring-2 focus:ring-lime rounded-none"
            >
              {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-white/50">One-liner * <span className="text-white/30">(max 280 chars)</span></Label>
          <Input data-testid="input-one-liner" required maxLength={280} value={form.one_liner} onChange={set("one_liner")} className={`mt-2 ${inputCls}`} placeholder="We build [what] for [who] so [outcome]." />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-white/50">Longer description <span className="text-white/30">(optional)</span></Label>
          <Textarea data-testid="input-long-desc" rows={6} value={form.long_description} onChange={set("long_description")} className="mt-2 bg-black border-white/10 text-white placeholder:text-white/40 rounded-none focus-visible:ring-lime" placeholder="Traction, team, why now, ask…" />
        </div>
        <div className="pt-4">
          <button
            data-testid="submit-application"
            type="submit"
            disabled={submitting}
            className="px-10 py-5 bg-lime text-black font-heading font-black uppercase tracking-wider hover:scale-[1.03] transition-transform disabled:opacity-50 disabled:hover:scale-100"
          >
            {submitting ? "Submitting…" : "Submit application →"}
          </button>
          <p className="mt-4 text-white/40 text-xs font-mono uppercase tracking-widest">By applying you agree to appear on this website, on LinkedIn, and possibly on other platforms, if shortlisted. You also agree to accept — should the host decide to invest — his direct investment into your company.</p>
        </div>
      </form>
    </div>
  );
}
